import type { BookingRepository, BusyInterval, RepoEvent } from './data.ts';
import { AppError } from './errors.ts';
import { iso, mapRow, supabase, toAppError, unwrap, type BookingRow } from './supabase.ts';
import { assertSlug, cached, parseBusiness, type TenantSummary } from './tenant.ts';
import type { Booking, BookingStatus, Business, NewBooking } from '../utils/validation.ts';

/** Shop config: profile from tenants.business_data, catalogue from the tables. */
const tenants = cached(async (slug) => {
  assertSlug(slug);
  const { data, error } = await supabase().rpc('get_tenant_config', { p_slug: slug });
  if (error) throw toAppError(error);
  if (!data) throw new AppError('not_found');
  return parseBusiness(data, slug);
});

export const loadTenant = (slug: string): Promise<Business> => tenants.get(slug);
export const invalidateTenant = (slug: string): void => void tenants.delete(slug);

export async function listTenants(): Promise<TenantSummary[]> {
  return unwrap(await supabase().rpc('list_tenants')) as TenantSummary[];
}

const POLL_MS = 60_000;

export class PublicRepository implements BookingRepository {
  constructor(private readonly business: Business) {}

  async getBusy(from: Date, to: Date): Promise<BusyInterval[]> {
    const rows = unwrap(
      await supabase().rpc('get_busy_slots', { p_slug: this.business.slug, p_from: from.toISOString(), p_to: to.toISOString() }),
    ) as { barber_id: string; starts_at: string; ends_at: string }[];
    return rows.map((r) => ({ barber_id: r.barber_id, starts_at: iso(r.starts_at), ends_at: iso(r.ends_at), status: 'confirmed' }));
  }

  async createBooking(input: NewBooking): Promise<Booking> {
    const service = this.business.services.find((s) => s.id === input.service_id);
    if (!service) throw new AppError('invalid');
    const row = unwrap(
      await supabase()
        .rpc('create_booking', {
          p_slug: this.business.slug,
          p_service_id: input.service_id,
          p_barber_id: input.barber_id,
          p_starts_at: input.starts_at,
          p_name: input.client_name,
          p_phone: input.client_phone,
          p_email: input.client_email ?? null,
          p_locale: input.locale,
        })
        .single(),
    ) as { id: string; cancel_token: string; starts_at: string; ends_at: string; price_cents: number; status: BookingStatus };
    return {
      ...input,
      id: row.id,
      tenant_slug: this.business.slug,
      starts_at: iso(row.starts_at),
      ends_at: iso(row.ends_at),
      duration_min: service.duration_min,
      price: row.price_cents / 100,
      status: row.status,
      cancel_token: row.cancel_token,
      created_at: new Date().toISOString(),
    };
  }

  async getBookings(refs: { id: string; cancel_token: string }[]): Promise<Booking[]> {
    if (refs.length === 0) return [];
    const tokens = new Map(refs.map((r) => [r.id, r.cancel_token]));
    const rows = unwrap(await supabase().rpc('get_my_bookings', { p_refs: refs })) as BookingRow[];
    return rows.map((r) => mapRow(r, this.business.slug, tokens.get(r.id)));
  }

  async cancelByClient(id: string, cancelToken: string): Promise<Booking> {
    unwrap(await supabase().rpc('cancel_booking', { p_id: id, p_token: cancelToken }));
    const [booking] = await this.getBookings([{ id, cancel_token: cancelToken }]);
    if (!booking) throw new AppError('not_found');
    return booking;
  }

  /** Anonymous visitors get no row events: refresh availability periodically. */
  subscribe(fn: (event: RepoEvent) => void): () => void {
    const refresh = () => {
      if (document.visibilityState === 'visible') fn({ type: 'refresh' });
    };
    const timer = setInterval(refresh, POLL_MS);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }
}

const repos = new Map<string, PublicRepository>();
export function publicRepository(business: Business): PublicRepository {
  let repo = repos.get(business.slug);
  if (!repo) repos.set(business.slug, (repo = new PublicRepository(business)));
  return repo;
}
